import React, { useState, useMemo, useCallback } from "react";
import RtlsMap from "./components/rtlsMap";
import Modal from "./components/Modal";
import ActivityHistory from "./components/modalContent/moduleMetricsModalComponents/activityHistory";
import ComparisonTool from "./components/modalContent/moduleMetricsModalComponents/comparisonTool";
import { useModuleMetrics } from "./hooks/useModuleMetrics";
import { useIsMobile } from "./hooks/useIsMobile";
import DisabledTooltip from "./components/disabledToolTip";
import "./moduleMetrics.css";
import "../animation.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Format an ISO-8601 datetime string to a short locale date string,
 * e.g. "2024-03-15T00:00:00.000Z" → "Mar 15, 2024".
 * Returns "-" for null / unparseable values.
 */
function formatDate(isoString) {
  if (!isoString) return "-";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FUNCTION_BUTTONS = [
  {
    id: "heatmap",
    label: "Heatmap",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="3" />
        <circle cx="12" cy="12" r="7" opacity="0.5" />
        <circle cx="12" cy="12" r="10" opacity="0.25" />
      </svg>
    ),
  },
  {
    id: "spaghetti",
    label: "Spaghetti Map",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
        <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
      </svg>
    ),
  },
  {
    id: "workflow",
    label: "Workflow Map",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="3" y="3" width="5" height="5" rx="1" />
        <rect x="16" y="3" width="5" height="5" rx="1" />
        <rect x="3" y="16" width="5" height="5" rx="1" />
        <rect x="16" y="16" width="5" height="5" rx="1" />
        <line x1="8" y1="5.5" x2="16" y2="5.5" />
        <line x1="5.5" y1="8" x2="5.5" y2="16" />
        <line x1="18.5" y1="8" x2="18.5" y2="16" />
        <line x1="8" y1="18.5" x2="16" y2="18.5" />
      </svg>
    ),
  },
  {
    id: "playback",
    label: "Playback",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  {
    id: "activity",
    label: "Activity History",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    id: "comparison",
    label: "Comparison Tool",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const ModuleMetricsPage = () => {
  // ── Hook - all data fetching, search state, and selection ──────────────────
  const {
    search,
    setSearch,
    dropdownOpen,
    setDropdownOpen,
    dropdownModules,
    selectedModule,
    handleSelectModule,
    handleClearSelection,
    moduleData,
    metricsLoading,
    metricsError,
    activityData,
    activityLoading,
    activityError,
    listLoading,
    listError,
  } = useModuleMetrics();

  // ── Local UI state (no data dependency) ───────────────────────────────────
  const [activeModal, setActiveModal] = useState(null);
  const [activeRtlsFn, setActiveRtlsFn] = useState(null);

  const isMobile = useIsMobile();
  const visibleButtons = isMobile
    ? FUNCTION_BUTTONS.filter((btn) => btn.modal)
    : FUNCTION_BUTTONS;

  // ── Derived values ─────────────────────────────────────────────────────────

  // RTLS filter query - uses real WoNum from the selected list row.
  const moduleFilterQuery = useMemo(() => {
    if (!selectedModule) return null;
    return `{obj.name} = "${selectedModule.WoNum}"`;
  }, [selectedModule]);

  // completionRate lives in moduleData (from the metrics fetch), not in the
  // list row - derive the colour only once data has arrived.
  const completionRate = moduleData?.moduleMetrics?.completionRate ?? null;
  const completionColor =
    completionRate === null
      ? "var(--mm-muted)"
      : completionRate >= 80
        ? "var(--mm-green)"
        : completionRate >= 50
          ? "var(--mm-amber)"
          : "var(--mm-red)";

  // ComparisonTool (and useComparisonTool) expect a flat, camelCase module
  // shape: { workOrder, itemNumber, property, sqft, currentStation,
  // completionRate }. Neither of our two source objects matches that shape
  // directly:
  //   - selectedModule is the raw modules-list row (PascalCase: WoNum,
  //     ItemNum, Sqft, Property), available immediately on selection.
  //   - moduleData is the module-metrics fetch result, nested under
  //     moduleInfo/moduleMetrics, and only available once that fetch resolves.
  // currentStation/completionRate only exist on moduleData, so they're null
  // until the metrics fetch completes - ComparisonTool's header already
  // renders "-" for null, so this is safe to pass through before that lands.
  const comparisonModule = useMemo(() => {
    if (!selectedModule) return null;
    return {
      workOrder: selectedModule.WoNum,
      itemNumber: selectedModule.ItemNum,
      property: selectedModule.Property,
      sqft: selectedModule.Sqft,
      currentStation: moduleData?.moduleMetrics?.currentStation ?? null,
      completionRate: moduleData?.moduleMetrics?.completionRate ?? null,
    };
  }, [selectedModule, moduleData]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setDropdownOpen(true);
  };

  // Wraps the hook's handler to also reset RTLS popup on new selection.
  const handleSelectModuleWithReset = (mod) => {
    handleSelectModule(mod);
    setActiveRtlsFn(null);
  };

  // Wraps the hook's clear handler to also reset RTLS popup.
  const handleClear = () => {
    handleClearSelection();
    setActiveRtlsFn(null);
  };

  // ── handleFunctionBtn ──────────────────────────────────────────────────────
  // Modal buttons open a local modal.
  // RTLS buttons toggle the filter popup:
  //   • Same button while active  → close (set null)
  //   • Different button          → swap to new function
  //   • Any RTLS button inactive  → open popup for that function
  const handleFunctionBtn = useCallback(
    (btnId) => {
      if (!selectedModule) return;

      if (btnId === "activity" || btnId === "comparison") {
        setActiveModal(btnId);
        return;
      }

      if (FUNCTION_BUTTONS.find((b) => b.id === btnId)?.rtls) {
        setActiveRtlsFn((prev) => (prev === btnId ? null : btnId));
      }
    },
    [selectedModule],
  );

  // Called by RtlsMap when the filter popup is closed from inside the map.
  const handleRtlsFnClose = useCallback(() => setActiveRtlsFn(null), []);

  // Called by RtlsMap after Apply.
  const handleRtlsFilterApply = useCallback((filters) => {
    console.log("[ModuleMetrics] RTLS filter applied:", filters);
  }, []);

  const closeModal = useCallback(() => setActiveModal(null), []);

  // ── Render helpers ─────────────────────────────────────────────────────────

  // Inline loading skeleton for a single metric tile value.
  const MetricSkeleton = () => (
    <span
      className="mm-metric-value mm-metric-value--skeleton"
      aria-hidden="true"
    >
      ···
    </span>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="mm-page page-enter">
      <div className="mm-page-header">
        <h1>Module Metrics</h1>
        <p className="mm-page-subtitle">
          Search and inspect individual module data and RTLS position
        </p>
      </div>

      <div className="mm-layout">
        {!isMobile && (
          <div className="mm-map-col">
            <RtlsMap
              isHomePage={false}
              filterQuery={moduleFilterQuery}
              activeRtlsFn={activeRtlsFn}
              rtlsSelectedObject={selectedModule?.WoNum ?? null}
              onRtlsFilterApply={handleRtlsFilterApply}
              onRtlsFnClose={handleRtlsFnClose}
            />
          </div>
        )}

        <div className="mm-right-col">
          {/* ── Search card ──────────────────────────────────────────────── */}
          <div
            className="mm-card mm-search-card stagger-item"
            style={{ "--i": 0 }}
          >
            <div className="mm-search-wrap">
              <div className="mm-search-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                className="mm-search-input"
                type="text"
                placeholder="Search by WO, Item Number, or Batch Number..."
                value={search}
                onChange={handleSearchChange}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                aria-label="Search modules"
                disabled={listLoading}
              />
              {search && (
                <button
                  className="mm-search-clear"
                  onClick={handleClear}
                  aria-label="Clear search"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            {/* List fetch error */}
            {listError && (
              <div className="mm-inline-error" role="alert">
                {listError}
              </div>
            )}

            {/* Dropdown - shown while open with results */}
            {dropdownOpen && !listLoading && dropdownModules.length > 0 && (
              <ul className="mm-dropdown" role="listbox">
                {dropdownModules.map((mod) => (
                  <li
                    key={mod.WoNum}
                    className="mm-dropdown-item"
                    onMouseDown={() => handleSelectModuleWithReset(mod)}
                    role="option"
                  >
                    <span className="mm-dropdown-wo">{mod.WoNum}</span>
                    <span className="mm-dropdown-meta">
                      {mod.ItemNum} &bull; {mod.BatchNum}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* No-results state - only when user has typed something */}
            {dropdownOpen &&
              !listLoading &&
              search.trim() &&
              dropdownModules.length === 0 && (
                <div className="mm-dropdown mm-dropdown--empty">
                  No modules match your search.
                </div>
              )}

            {/* List loading indicator inside dropdown area */}
            {dropdownOpen && listLoading && (
              <div className="mm-dropdown mm-dropdown--loading">
                Loading modules…
              </div>
            )}
          </div>

          {/* ── Empty state ──────────────────────────────────────────────── */}
          {!selectedModule && (
            <div className="mm-empty-state stagger-item" style={{ "--i": 1 }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <path d="M9 9h6M9 12h6M9 15h4" />
              </svg>
              <p>Search for a module to view details</p>
              <span>
                Enter a work order, item number, or batch number above
              </span>
            </div>
          )}

          {/* ── Selected-module sections ──────────────────────────────────── */}
          {selectedModule && (
            <>
              {/* Section 1: Actions */}
              <div className="mm-card stagger-item" style={{ "--i": 1 }}>
                <h2 className="mm-section-title">Actions</h2>
                <div className="mm-fn-grid">
                  {visibleButtons.map((btn) => {
                    const isRtlsActive = btn.rtls && activeRtlsFn === btn.id;
                    return (
                      <DisabledTooltip
                        key={btn.id}
                        disabled={!btn.modal && !btn.rtls}
                      >
                        <button
                          className={[
                            "mm-fn-btn",
                            btn.modal ? "mm-fn-btn--modal" : "",
                            isRtlsActive ? "mm-fn-btn--active" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => handleFunctionBtn(btn.id)}
                          title={btn.subtitle}
                        >
                          <div className="mm-fn-btn-icon">{btn.icon}</div>
                          <div className="mm-fn-btn-text">
                            <span className="mm-fn-btn-label">{btn.label}</span>
                            <span className="mm-fn-btn-sub">
                              {btn.subtitle}
                            </span>
                          </div>
                        </button>
                      </DisabledTooltip>
                    );
                  })}
                </div>
              </div>

              {/* Section 2: Module Information
                  Source: selectedModule (modules-list row) - available
                  immediately on selection, no metrics fetch needed. */}
              <div className="mm-card stagger-item" style={{ "--i": 2 }}>
                <h2 className="mm-section-title">Module Information</h2>
                <div className="mm-info-grid">
                  <div className="mm-info-item">
                    <span className="mm-info-label">Work Order</span>
                    <span className="mm-info-value">
                      {selectedModule.WoNum ?? "-"}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Item Number</span>
                    <span className="mm-info-value">
                      {selectedModule.ItemNum ?? "-"}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Batch Number</span>
                    <span className="mm-info-value">
                      {selectedModule.BatchNum ?? "-"}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Online Date</span>
                    <span className="mm-info-value">
                      {formatDate(selectedModule.actualOnlineDate)}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Square Footage</span>
                    <span className="mm-info-value">
                      {selectedModule.Sqft != null
                        ? `${Number(selectedModule.Sqft).toLocaleString()} sqft`
                        : "-"}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Property</span>
                    <span className="mm-info-value">
                      {selectedModule.Property ?? "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 3: Module Metrics
                  Source: moduleData (module-metrics fetch).
                  Shows skeletons while loading, error banner on failure,
                  then real values once data arrives. */}
              <div className="mm-card stagger-item" style={{ "--i": 3 }}>
                <h2 className="mm-section-title">Module Metrics</h2>

                {metricsError && (
                  <div className="mm-inline-error" role="alert">
                    {metricsError}
                  </div>
                )}

                <div className="mm-metrics-grid">
                  {/* Current Station */}
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Current Station</span>
                    {metricsLoading ? (
                      <MetricSkeleton />
                    ) : (
                      <span className="mm-metric-value">
                        {moduleData?.moduleMetrics?.currentStation ?? "-"}
                      </span>
                    )}
                  </div>

                  {/* Jobs Redone */}
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Jobs Redone</span>
                    {metricsLoading ? (
                      <MetricSkeleton />
                    ) : (
                      <span
                        className={[
                          "mm-metric-value",
                          moduleData?.moduleMetrics?.jobsRedone > 0
                            ? "mm-metric-value--alert"
                            : "mm-metric-value--ok",
                        ].join(" ")}
                      >
                        {moduleData?.moduleMetrics?.jobsRedone ?? "-"}
                      </span>
                    )}
                  </div>

                  {/* Completion Rate */}
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Completion Rate</span>
                    {metricsLoading ? (
                      <MetricSkeleton />
                    ) : (
                      <span
                        className="mm-metric-value"
                        style={{ color: completionColor }}
                      >
                        {completionRate !== null ? `${completionRate}%` : "-"}
                      </span>
                    )}
                  </div>

                  {/* Time on Line */}
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Time on Line</span>
                    {metricsLoading ? (
                      <MetricSkeleton />
                    ) : (
                      <span className="mm-metric-value">
                        {moduleData?.moduleMetrics?.timeOnLine ?? "-"}
                      </span>
                    )}
                  </div>

                  {/* Total Manned Hours */}
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Total Man Hours</span>
                    {metricsLoading ? (
                      <MetricSkeleton />
                    ) : (
                      <span className="mm-metric-value">
                        {moduleData?.moduleMetrics?.totalMannedHours != null
                          ? Number(
                              moduleData.moduleMetrics.totalMannedHours,
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 1,
                            })
                          : "-"}
                      </span>
                    )}
                  </div>

                  {/* Scheduled Completion */}
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">
                      Scheduled Completion
                    </span>
                    {metricsLoading ? (
                      <MetricSkeleton />
                    ) : (
                      <span className="mm-metric-value">
                        {formatDate(
                          moduleData?.moduleInfo?.scheduledOfflineDate,
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      <Modal
        isOpen={activeModal === "activity"}
        onClose={closeModal}
        title="Activity History"
        size="large"
      >
        {selectedModule && (
          <ActivityHistory
            moduleInfo={moduleData?.moduleInfo}
            activityData={activityData}
            activityLoading={activityLoading}
            activityError={activityError}
          />
        )}
      </Modal>
      <Modal
        isOpen={activeModal === "comparison"}
        onClose={closeModal}
        title="Comparison Tool"
        size="large"
      >
        {selectedModule && <ComparisonTool module={comparisonModule} />}
      </Modal>
    </div>
  );
};

export default ModuleMetricsPage;
